import React, { FunctionComponent, useState } from "react";
import { MosaicElementType, WeatherForecastType, WeatherParameters, uploadImage } from "../../../../api/mosaicApi";
import { Button } from "../../../components/Button";
import FormCheckbox from "../../../components/formRow/FormCheckbox";
import { FormInput, FormInputColorpicker, FormInputNumber, FormRow, FormSelect, SelectOption } from "../../../components/formRow/FormRow";
import { SelectAvatarButton } from "../../../components/selectFileButton/SelectFileButton";
import SvgSelector from "../../../components/svgSelector/SvgSelector";

export interface WeatherWidgetData {
    type: MosaicElementType;
    startColumn: number;
    width: number;
    startRow: number;
    height: number;
    name: string;
    parameters: WeatherParameters;
}

interface WeatherWidgetProps {
    columnStart: number | string;
    width: number | string;
    rowStart: number | string;
    height: number | string;
    onColumnStartChange: (value: number | string) => void;
    onWidthChange: (value: number | string) => void;
    onRowStartChange: (value: number | string) => void;
    onHeightChange: (value: number | string) => void;
    isOutOfLimit: boolean;
    maxElements: number;
    currentCount: number;
    onAddWidget: (data: WeatherWidgetData) => void;
    validationError?: string;
    onBackgroundImageUrlChange?: (url: string) => void;
}

const ForecastTypeOptions: SelectOption[] = [
    { name: 'Hourly', value: '0' },
    { name: 'Daily', value: '1' }
];

export const WeatherWidget: FunctionComponent<WeatherWidgetProps> = (props) => {
    const {
        columnStart,
        width,
        rowStart,
        height,
        onColumnStartChange,
        onWidthChange,
        onRowStartChange,
        onHeightChange,
        isOutOfLimit,
        maxElements,
        currentCount,
        onAddWidget,
        validationError,
        onBackgroundImageUrlChange
    } = props;

    // Loading state
    const [isLoading, setIsLoading] = useState<boolean>(false);

    // Background Image
    const [backgroundImageBlobName, setBackgroundImageBlobName] = useState<string>('');
    const [backgroundImageUrl, setBackgroundImageUrl] = useState<string>('');

    // Basic settings
    const [city, setCity] = useState<string>('');
    const [showCurrentWeather, setShowCurrentWeather] = useState<boolean>(true);
    const [showForecast, setShowForecast] = useState<boolean>(true);
    const [forecastType, setForecastType] = useState<number>(WeatherForecastType.Hourly);
    const [itemCount, setItemCount] = useState<number | string>(5);
    const [updateIntervalMinutes, setUpdateIntervalMinutes] = useState<number | string>(30);

    // Colors
    const [textColour, setTextColour] = useState<string>('#ffffff');
    const [backgroundColour, setBackgroundColour] = useState<string>('#000000');

    // Font sizes
    const [cityNameFontSize, setCityNameFontSize] = useState<number | string>('');
    const [weatherDescriptionFontSize, setWeatherDescriptionFontSize] = useState<number | string>('');
    const [currentTemperatureFontSize, setCurrentTemperatureFontSize] = useState<number | string>('');
    const [windFontSize, setWindFontSize] = useState<number | string>('');
    const [aqiFontSize, setAqiFontSize] = useState<number | string>('');
    const [forecastTimeFontSize, setForecastTimeFontSize] = useState<number | string>('');
    const [forecastTempFontSize, setForecastTempFontSize] = useState<number | string>('');

    // Icon sizes
    const [forecastIconSize, setForecastIconSize] = useState<number | string>('');
    const [currentIconSize, setCurrentIconSize] = useState<number | string>('');

    // Padding & Spacing
    const [leftPadding, setLeftPadding] = useState<number | string>('');
    const [spaceBetweenCurrentWeatherAndAirQuality, setSpaceBetweenCurrentWeatherAndAirQuality] = useState<number | string>('');
    const [spaceBetweenAirQualityAndForecast, setSpaceBetweenAirQualityAndForecast] = useState<number | string>('');

    // AQI visibility
    const [showAqi, setShowAqi] = useState<boolean>(false);

    // Pollen visibility
    const [showGrassPollen, setShowGrassPollen] = useState<boolean>(false);
    const [showBirchPollen, setShowBirchPollen] = useState<boolean>(false);
    const [showRagweedPollen, setShowRagweedPollen] = useState<boolean>(false);
    const [showAlderPollen, setShowAlderPollen] = useState<boolean>(false);
    const [showMugwortPollen, setShowMugwortPollen] = useState<boolean>(false);
    const [showOlivePollen, setShowOlivePollen] = useState<boolean>(false);

    // Position handlers
    const handleColumnStartChange = (value: string) => {
        if (value === "" || +value < 1) onColumnStartChange('');
        else onColumnStartChange(+value);
    };
    const handleWidthChange = (value: string) => {
        if (value === "" || +value < 1) onWidthChange('');
        else onWidthChange(+value);
    };
    const handleRowStartChange = (value: string) => {
        if (value === "" || +value < 1) onRowStartChange('');
        else onRowStartChange(+value);
    };
    const handleHeightChange = (value: string) => {
        if (value === "" || +value < 1) onHeightChange('');
        else onHeightChange(+value);
    };

    const handleColumnStartBlur = (value: string) => {
        value === "" && onColumnStartChange(1);
    };
    const handleWidthBlur = (value: string) => {
        value === "" && onWidthChange(1);
    };
    const handleRowStartBlur = (value: string) => {
        value === "" && onRowStartChange(1);
    };
    const handleHeightBlur = (value: string) => {
        value === "" && onHeightChange(1);
    };

    // Input handlers
    const handleCityChange = (e: React.ChangeEvent<HTMLInputElement>) => setCity(e.target.value);
    const handleForecastTypeChange = (value: string) => setForecastType(+value);
    const handleItemCountChange = (value: string) => setItemCount(value === '' ? '' : +value);
    const handleUpdateIntervalChange = (value: string) => setUpdateIntervalMinutes(value === '' ? '' : +value);

    // Color handlers
    const handleTextColourChange = (value: string) => setTextColour(value);
    const handleBackgroundColourChange = (value: string) => setBackgroundColour(value);

    // Font size handlers
    const handleCityNameFontSizeChange = (value: string) => setCityNameFontSize(value === '' ? '' : +value);
    const handleWeatherDescriptionFontSizeChange = (value: string) => setWeatherDescriptionFontSize(value === '' ? '' : +value);
    const handleCurrentTemperatureFontSizeChange = (value: string) => setCurrentTemperatureFontSize(value === '' ? '' : +value);
    const handleWindFontSizeChange = (value: string) => setWindFontSize(value === '' ? '' : +value);
    const handleAqiFontSizeChange = (value: string) => setAqiFontSize(value === '' ? '' : +value);
    const handleForecastTimeFontSizeChange = (value: string) => setForecastTimeFontSize(value === '' ? '' : +value);
    const handleForecastTempFontSizeChange = (value: string) => setForecastTempFontSize(value === '' ? '' : +value);

    // Icon size handlers
    const handleForecastIconSizeChange = (value: string) => setForecastIconSize(value === '' ? '' : +value);
    const handleCurrentIconSizeChange = (value: string) => setCurrentIconSize(value === '' ? '' : +value);

    // Padding & Spacing handlers
    const handleLeftPaddingChange = (value: string) => setLeftPadding(value === '' ? '' : +value);
    const handleSpaceBetweenCurrentWeatherAndAirQualityChange = (value: string) => setSpaceBetweenCurrentWeatherAndAirQuality(value === '' ? '' : +value);
    const handleSpaceBetweenAirQualityAndForecastChange = (value: string) => setSpaceBetweenAirQualityAndForecast(value === '' ? '' : +value);

    // Background Image handler
    const onBackgroundImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const formData = new FormData();
            setIsLoading(true);
            formData.append("Recource", event.target.files[0]);
            let res = await uploadImage(formData);
            if (res.status === 200) {
                setBackgroundImageBlobName(res.data.data.name);
                setBackgroundImageUrl(res.data.data.url);
                onBackgroundImageUrlChange?.(res.data.data.url);
                event.target.value = '';
            }
            setIsLoading(false);
        }
    };

    const resetState = () => {
        setCity('');
        setShowCurrentWeather(true);
        setShowForecast(true);
        setForecastType(WeatherForecastType.Hourly);
        setItemCount(5);
        setUpdateIntervalMinutes(30);
        setBackgroundImageBlobName('');
        setBackgroundImageUrl('');
        onBackgroundImageUrlChange?.('');
        setTextColour('#ffffff');
        setBackgroundColour('#000000');
        setCityNameFontSize('');
        setWeatherDescriptionFontSize('');
        setCurrentTemperatureFontSize('');
        setWindFontSize('');
        setAqiFontSize('');
        setForecastTimeFontSize('');
        setForecastTempFontSize('');
        setForecastIconSize('');
        setCurrentIconSize('');
        setLeftPadding('');
        setSpaceBetweenCurrentWeatherAndAirQuality('');
        setSpaceBetweenAirQualityAndForecast('');
        setShowAqi(false);
        setShowGrassPollen(false);
        setShowBirchPollen(false);
        setShowRagweedPollen(false);
        setShowAlderPollen(false);
        setShowMugwortPollen(false);
        setShowOlivePollen(false);
    };

    const handleAddWidget = () => {
        const parameters: WeatherParameters = {
            type: "weather",
            city: city,
            showCurrentWeather: showCurrentWeather ? undefined : false,
            showForecast: showForecast ? undefined : false,
            forecastType: forecastType,
            itemCount: itemCount === '' ? undefined : +itemCount,
            updateIntervalMinutes: updateIntervalMinutes === '' ? undefined : +updateIntervalMinutes,
            backgroundImageRecourceBlobName: backgroundImageBlobName || undefined,
            backgroundImageUrl: backgroundImageUrl || undefined,
            textColour: textColour,
            backgroundColour: backgroundColour,
            cityNameFontSize: cityNameFontSize === '' ? undefined : +cityNameFontSize,
            weatherDescriptionFontSize: weatherDescriptionFontSize === '' ? undefined : +weatherDescriptionFontSize,
            currentTemperatureFontSize: currentTemperatureFontSize === '' ? undefined : +currentTemperatureFontSize,
            windFontSize: windFontSize === '' ? undefined : +windFontSize,
            aqiFontSize: aqiFontSize === '' ? undefined : +aqiFontSize,
            forecastTimeFontSize: forecastTimeFontSize === '' ? undefined : +forecastTimeFontSize,
            forecastTempFontSize: forecastTempFontSize === '' ? undefined : +forecastTempFontSize,
            forecastIconSize: forecastIconSize === '' ? undefined : +forecastIconSize,
            currentIconSize: currentIconSize === '' ? undefined : +currentIconSize,
            leftPadding: leftPadding === '' ? undefined : +leftPadding,
            spaceBetweenCurrentWeatherAndAirQuality: spaceBetweenCurrentWeatherAndAirQuality === '' ? undefined : +spaceBetweenCurrentWeatherAndAirQuality,
            spaceBetweenAirQualityAndForecast: spaceBetweenAirQualityAndForecast === '' ? undefined : +spaceBetweenAirQualityAndForecast,
            showAqi: showAqi || undefined,
            showGrassPollen: showGrassPollen || undefined,
            showBirchPollen: showBirchPollen || undefined,
            showRagweedPollen: showRagweedPollen || undefined,
            showAlderPollen: showAlderPollen || undefined,
            showMugwortPollen: showMugwortPollen || undefined,
            showOlivePollen: showOlivePollen || undefined
        };

        onAddWidget({
            type: MosaicElementType.Weather,
            startColumn: +columnStart,
            width: +width,
            startRow: +rowStart,
            height: +height,
            name: city,
            parameters: parameters
        });

        resetState();
    };

    const rowClassnames = `mosaic-settings-add-elem ${isOutOfLimit ? 'out-of-limit' : ''}`;
    const fixedHeight = '180px';

    return (
        <>
            <div style={{ minHeight: fixedHeight }}>
                {/* Position Controls */}
                <div className={rowClassnames}>
                    {isOutOfLimit && (
                        <span className="info-message">
                            {`You can have ${maxElements} elements of this type`}
                        </span>
                    )}
                    <FormRow>
                        <FormInputNumber
                            id="weather-column-start"
                            title="Column Start"
                            isError={false}
                            errorMessage="Please enter column start"
                            onChangeValue={handleColumnStartChange}
                            onBlurFires={handleColumnStartBlur}
                            onChange={() => {}}
                            value={columnStart}
                        />
                    </FormRow>
                    <FormRow>
                        <FormInputNumber
                            id="weather-width"
                            title="Width"
                            isError={false}
                            errorMessage="Please enter width"
                            onChangeValue={handleWidthChange}
                            onBlurFires={handleWidthBlur}
                            onChange={() => {}}
                            value={width}
                        />
                    </FormRow>
                    <FormRow>
                        <FormInputNumber
                            id="weather-row-start"
                            title="Row Start"
                            isError={false}
                            errorMessage="Please enter row start"
                            onChangeValue={handleRowStartChange}
                            onBlurFires={handleRowStartBlur}
                            onChange={() => {}}
                            value={rowStart}
                        />
                    </FormRow>
                    <FormRow>
                        <FormInputNumber
                            id="weather-height"
                            title="Height"
                            isError={false}
                            errorMessage="Please enter height"
                            onChangeValue={handleHeightChange}
                            onBlurFires={handleHeightBlur}
                            onChange={() => {}}
                            value={height}
                        />
                    </FormRow>
                </div>
                {validationError && <span className="error">{validationError}</span>}
                
                {!isOutOfLimit && (
                    <div className="weather-widget" style={{ display: 'block' }}>
                        {/* Basic Settings */}
                        <div className="d-flex gap15">
                            <FormRow>
                                <FormInput
                                    errorMessage="Please enter city"
                                    id="weather_city"
                                    isError={false}
                                    title="City"
                                    onChange={handleCityChange}
                                    value={city}
                                    placeholderTxt="Enter city name"
                                />
                            </FormRow>
                        </div>

                        <div className="d-flex gap15">
                            <FormRow>
                                <FormSelect
                                    id="weather_forecast_type"
                                    title="Forecast Type"
                                    selectedValue={forecastType.toString()}
                                    isError={false}
                                    errorMessage="Error"
                                    options={ForecastTypeOptions}
                                    onChange={handleForecastTypeChange}
                                    hideDefaultValue={true}
                                />
                            </FormRow>
                            <FormRow>
                                <FormInputNumber
                                    id="weather_item_count"
                                    title="Item Count"
                                    isError={false}
                                    errorMessage="Please enter item count"
                                    onChangeValue={handleItemCountChange}
                                    onChange={() => {}}
                                    value={itemCount}
                                />
                            </FormRow>
                            <FormRow>
                                <FormInputNumber
                                    id="weather_update_interval"
                                    title="Update Interval (min)"
                                    isError={false}
                                    errorMessage="Please enter update interval"
                                    onChangeValue={handleUpdateIntervalChange}
                                    onChange={() => {}}
                                    value={updateIntervalMinutes}
                                />
                            </FormRow>
                        </div>

                        {/* Background Image */}
                        <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>Background Image</h4>
                        <div className="d-flex gap15 align-items-center">
                            <FormRow>
                                <div style={{ paddingTop: '15px' }}>
                                    <SelectAvatarButton id="weather-bg" title="Upload Background" onChange={onBackgroundImageChange} />
                                </div>
                            </FormRow>
                            {backgroundImageUrl && (
                                <img 
                                    className="mosaic-img" 
                                    src={backgroundImageUrl} 
                                    alt="Background preview" 
                                    style={{ maxHeight: '100px', width: 'auto', objectFit: 'contain' }} 
                                />
                            )}
                        </div>

                        {/* Colors */}
                        <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>Colors</h4>
                        <div className="d-flex gap15">
                            <FormRow>
                                <FormInputColorpicker
                                    id="weather_text_colour"
                                    title="Text Color"
                                    isError={false}
                                    errorMessage="Please enter text color"
                                    onChangeValue={handleTextColourChange}
                                    onChange={() => {}}
                                    value={textColour}
                                />
                            </FormRow>
                            <FormRow>
                                <FormInputColorpicker
                                    id="weather_background_colour"
                                    title="Background Color"
                                    isError={false}
                                    errorMessage="Please enter background color"
                                    onChangeValue={handleBackgroundColourChange}
                                    onChange={() => {}}
                                    value={backgroundColour}
                                />
                            </FormRow>
                        </div>

                        {/* Font Sizes - Current Weather */}
                        <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>Font Sizes - Current Weather</h4>
                        <div className="d-flex gap15">
                            <FormRow>
                                <FormInputNumber
                                    id="weather_city_name_font_size"
                                    title="City Name"
                                    isError={false}
                                    errorMessage="Please enter font size"
                                    onChangeValue={handleCityNameFontSizeChange}
                                    onChange={() => {}}
                                    value={cityNameFontSize}
                                />
                            </FormRow>
                            <FormRow>
                                <FormInputNumber
                                    id="weather_description_font_size"
                                    title="Description"
                                    isError={false}
                                    errorMessage="Please enter font size"
                                    onChangeValue={handleWeatherDescriptionFontSizeChange}
                                    onChange={() => {}}
                                    value={weatherDescriptionFontSize}
                                />
                            </FormRow>
                            <FormRow>
                                <FormInputNumber
                                    id="weather_current_temp_font_size"
                                    title="Temperature"
                                    isError={false}
                                    errorMessage="Please enter font size"
                                    onChangeValue={handleCurrentTemperatureFontSizeChange}
                                    onChange={() => {}}
                                    value={currentTemperatureFontSize}
                                />
                            </FormRow>
                            <FormRow>
                                <FormInputNumber
                                    id="weather_wind_font_size"
                                    title="Wind"
                                    isError={false}
                                    errorMessage="Please enter font size"
                                    onChangeValue={handleWindFontSizeChange}
                                    onChange={() => {}}
                                    value={windFontSize}
                                />
                            </FormRow>
                            <FormRow>
                                <FormInputNumber
                                    id="weather_aqi_font_size"
                                    title="AQI"
                                    isError={false}
                                    errorMessage="Please enter font size"
                                    onChangeValue={handleAqiFontSizeChange}
                                    onChange={() => {}}
                                    value={aqiFontSize}
                                />
                            </FormRow>
                        </div>

                        {/* Font Sizes - Forecast */}
                        <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>Font Sizes - Forecast</h4>
                        <div className="d-flex gap15">
                            <FormRow>
                                <FormInputNumber
                                    id="weather_forecast_time_font_size"
                                    title="Time"
                                    isError={false}
                                    errorMessage="Please enter font size"
                                    onChangeValue={handleForecastTimeFontSizeChange}
                                    onChange={() => {}}
                                    value={forecastTimeFontSize}
                                />
                            </FormRow>
                            <FormRow>
                                <FormInputNumber
                                    id="weather_forecast_temp_font_size"
                                    title="Temperature"
                                    isError={false}
                                    errorMessage="Please enter font size"
                                    onChangeValue={handleForecastTempFontSizeChange}
                                    onChange={() => {}}
                                    value={forecastTempFontSize}
                                />
                            </FormRow>
                        </div>

                        {/* Icon Sizes */}
                        <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>Icon Sizes</h4>
                        <div className="d-flex gap15">
                            <FormRow>
                                <FormInputNumber
                                    id="weather_current_icon_size"
                                    title="Current Icon Size"
                                    isError={false}
                                    errorMessage="Please enter icon size"
                                    onChangeValue={handleCurrentIconSizeChange}
                                    onChange={() => {}}
                                    value={currentIconSize}
                                />
                            </FormRow>
                            <FormRow>
                                <FormInputNumber
                                    id="weather_forecast_icon_size"
                                    title="Forecast Icon Size"
                                    isError={false}
                                    errorMessage="Please enter icon size"
                                    onChangeValue={handleForecastIconSizeChange}
                                    onChange={() => {}}
                                    value={forecastIconSize}
                                />
                            </FormRow>
                        </div>

                        {/* Padding & Spacing */}
                        <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>Padding & Spacing</h4>
                        <div className="d-flex gap15">
                            <FormRow>
                                <FormInputNumber
                                    id="weather_left_padding"
                                    title="Left Padding"
                                    isError={false}
                                    errorMessage="Please enter padding"
                                    onChangeValue={handleLeftPaddingChange}
                                    onChange={() => {}}
                                    value={leftPadding}
                                />
                            </FormRow>
                            <FormRow>
                                <FormInputNumber
                                    id="weather_space_current_aqi"
                                    title="Space: Weather - AQI"
                                    isError={false}
                                    errorMessage="Please enter spacing"
                                    onChangeValue={handleSpaceBetweenCurrentWeatherAndAirQualityChange}
                                    onChange={() => {}}
                                    value={spaceBetweenCurrentWeatherAndAirQuality}
                                />
                            </FormRow>
                            <FormRow>
                                <FormInputNumber
                                    id="weather_space_aqi_forecast"
                                    title="Space: AQI - Forecast"
                                    isError={false}
                                    errorMessage="Please enter spacing"
                                    onChangeValue={handleSpaceBetweenAirQualityAndForecastChange}
                                    onChange={() => {}}
                                    value={spaceBetweenAirQualityAndForecast}
                                />
                            </FormRow>
                        </div>

                        {/* Section Visibility */}
                        <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>Section Visibility</h4>
                        <div className="d-flex gap15" style={{ flexWrap: 'wrap' }}>
                            <div className="d-flex align-items-center" style={{ marginRight: '20px' }}>
                                <FormCheckbox id="weather_show_current" checked={showCurrentWeather} onChange={setShowCurrentWeather} />
                                <span style={{ marginLeft: '8px' }}>Current Weather</span>
                            </div>
                            <div className="d-flex align-items-center" style={{ marginRight: '20px' }}>
                                <FormCheckbox id="weather_show_forecast" checked={showForecast} onChange={setShowForecast} />
                                <span style={{ marginLeft: '8px' }}>Forecast</span>
                            </div>
                        </div>

                        {/* Air Quality Visibility */}
                        <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>Air Quality Visibility</h4>
                        <div className="d-flex gap15" style={{ flexWrap: 'wrap' }}>
                            <div className="d-flex align-items-center" style={{ marginRight: '20px' }}>
                                <FormCheckbox id="weather_show_aqi" checked={showAqi} onChange={setShowAqi} />
                                <span style={{ marginLeft: '8px' }}>AQI</span>
                            </div>
                            <div className="d-flex align-items-center" style={{ marginRight: '20px' }}>
                                <FormCheckbox id="weather_grass_pollen" checked={showGrassPollen} onChange={setShowGrassPollen} />
                                <span style={{ marginLeft: '8px' }}>Grass Pollen</span>
                            </div>
                            <div className="d-flex align-items-center" style={{ marginRight: '20px' }}>
                                <FormCheckbox id="weather_birch_pollen" checked={showBirchPollen} onChange={setShowBirchPollen} />
                                <span style={{ marginLeft: '8px' }}>Birch Pollen</span>
                            </div>
                            <div className="d-flex align-items-center" style={{ marginRight: '20px' }}>
                                <FormCheckbox id="weather_ragweed_pollen" checked={showRagweedPollen} onChange={setShowRagweedPollen} />
                                <span style={{ marginLeft: '8px' }}>Ragweed Pollen</span>
                            </div>
                            <div className="d-flex align-items-center" style={{ marginRight: '20px' }}>
                                <FormCheckbox id="weather_alder_pollen" checked={showAlderPollen} onChange={setShowAlderPollen} />
                                <span style={{ marginLeft: '8px' }}>Alder Pollen</span>
                            </div>
                            <div className="d-flex align-items-center" style={{ marginRight: '20px' }}>
                                <FormCheckbox id="weather_mugwort_pollen" checked={showMugwortPollen} onChange={setShowMugwortPollen} />
                                <span style={{ marginLeft: '8px' }}>Mugwort Pollen</span>
                            </div>
                            <div className="d-flex align-items-center" style={{ marginRight: '20px' }}>
                                <FormCheckbox id="weather_olive_pollen" checked={showOlivePollen} onChange={setShowOlivePollen} />
                                <span style={{ marginLeft: '8px' }}>Olive Pollen</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <div className="mosaic-settings-add-elem align-items-center" style={{ marginTop: '20px', marginBottom: '15px' }}>
                <Button
                    color="green"
                    label="Add Weather Widget"
                    onClick={handleAddWidget}
                    isDisabled={isOutOfLimit || city === ''}
                />
            </div>
        </>
    );
};

// Helper functions for Weather widget
export const getWeatherWidgetIcon = () => <SvgSelector title="weather" />;
export const getWeatherWidgetName = () => 'Weather';
export const getWeatherWidgetLimit = () => 3;

export default WeatherWidget;
